import { useFormContext } from 'react-hook-form';

import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRosterTemplatesCatalog } from 'hooks/incident/useRoster';
import { useRosterTemplateLibrary } from 'hooks/rosterTemplateLibrary/rosterTemplateLibraryHooks';
import {
  useFilledRosterTemplates,
  type FilledRosterTemplate,
} from 'hooks/rosterTemplateLibrary/filledRosterTemplateHooks';
import type { RosterTemplateNodeDto } from 'components/IncidentConfig/roster/templates/rosterTemplateTypes';

import type { CreateIncidentFormValues } from './createIncidentModel';
import { useCreateRosterDraft } from './CreateRosterDraftContext';

const NONE = '__none__';

/**
 * Optional HWCG choice on Build Team. Applying one replaces the draft chart
 * with that structure and seats the people saved on it.
 */
export function FilledRosterTemplatePicker() {
  const { setValue, watch } = useFormContext<CreateIncidentFormValues>();
  const selectedId = watch('hwcg_filled_roster_template_id');
  const templates = useFilledRosterTemplates();
  const library = useRosterTemplateLibrary();
  const catalog = useRosterTemplatesCatalog();
  const { applyFilledRoster } = useCreateRosterDraft();

  const apply = (template: FilledRosterTemplate | null) => {
    if (!template) {
      setValue('hwcg_filled_roster_template_id', null, { shouldDirty: true });
      setValue('hwcg_roster_externals', [], { shouldDirty: true });
      return;
    }
    const nodes = nodesFor(
      template,
      library.data ?? [],
      catalog.data?.templates ?? []
    );
    if (!nodes.length) return;
    const people = template.assignments.flatMap((row) =>
      row.userGuid
        ? [
            {
              seatName: row.seatName,
              user_guid: row.userGuid,
              name: row.name,
              email_address: row.email,
            },
          ]
        : []
    );
    applyFilledRoster({
      nodes,
      slug: template.sourceSlug || template.sourceTemplateId || template.id,
      name: template.name,
      people,
    });
    setValue(
      'hwcg_roster_externals',
      template.assignments
        .filter((row) => !row.userGuid)
        .map((row) => ({
          seatName: row.seatName,
          name: row.name,
          email: row.email,
          phone: row.phone,
        })),
      { shouldDirty: true }
    );
    setValue('hwcg_filled_roster_template_id', template.id, {
      shouldDirty: true,
    });
  };

  return (
    <Field className="tw:shrink-0">
      <FieldLabel>Filled roster template</FieldLabel>
      <Select
        value={selectedId || NONE}
        onValueChange={(value) => {
          if (value === NONE) {
            apply(null);
            return;
          }
          apply(
            (templates.data ?? []).find((template) => template.id === value) ??
              null
          );
        }}
      >
        <SelectTrigger className="tw:w-full">
          <SelectValue placeholder="None" />
        </SelectTrigger>
        <SelectContent className="dark">
          <SelectItem value={NONE}>None</SelectItem>
          {(templates.data ?? []).map((template) => (
            <SelectItem key={template.id} value={template.id}>
              {template.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FieldDescription>
        Optional. People with accounts are placed on the seats and included in
        activation requests. Contact-list people without an account are named on
        the seat and emailed or texted when the incident is created.
      </FieldDescription>
    </Field>
  );
}

function nodesFor(
  template: FilledRosterTemplate,
  library: { id: string; nodes: RosterTemplateNodeDto[] }[],
  catalog: { slug: string; nodes?: RosterTemplateNodeDto[] }[]
): RosterTemplateNodeDto[] {
  if (template.sourceTemplateId) {
    return (
      library.find((entry) => entry.id === template.sourceTemplateId)?.nodes ??
      []
    );
  }
  if (template.sourceSlug) {
    return (
      catalog.find((entry) => entry.slug === template.sourceSlug)?.nodes ?? []
    );
  }
  return [];
}
